/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.
*/

import type { Route, Routes } from "@angular/router";
import type { generatedRoutes } from "@app/app.processed";
import { routes } from "@app/app.routes";

export interface CbAugmentedRoute extends Omit<Route, "children" | "path"> {
	showSidebar?: boolean;
	realtimeCollaboration?: boolean;
	features?: string[];
	children?: CbAugmentedRoutes;
	path?: string;
}

export type CbAugmentedRoutes = {
	root?: CbAugmentedRoute;
} & Record<string, CbAugmentedRoute>;

type RoutePaths<T> = {
	[K in keyof T]: T[K] extends { children: infer C }
	? { path: string } & Omit<RoutePaths<C>, "root">
	: { path: string };
};

type CbRoutesPaths = RoutePaths<typeof generatedRoutes>;

// A helper type to extract the children keys of a given segment
export const CbRoutesUtils = {
	paths: getCalculatedPaths(),
	angularRoutes: convertCustomConfigToRoutes(routes),
	createUrl: (
		path: string,
		config?: { withPrefix?: boolean; args?: Record<string, string | number> },
	) => {
		// Initialize the navigation path with the provided path value
		let navigationPath = "";

		// Iterate over the navigation path and search into the original path for the dynamic segments
		let currentRoutes: CbAugmentedRoutes = routes;
		for (const k of path.split("/")) {
			if (k === "") continue;

			// Convert kebab-case to camelCase for route lookup
			const camelCaseKey = k.replace(/-([a-z])/g, (_, letter) =>
				letter.toUpperCase(),
			);

			if (currentRoutes[camelCaseKey]) {
				if (currentRoutes[camelCaseKey].path) {
					navigationPath += `/${currentRoutes[camelCaseKey].path}`;
				} else {
					navigationPath += `/${k}`;
				}

				if (currentRoutes[camelCaseKey].children !== undefined) {
					// biome-ignore lint/style/noNonNullAssertion: <explanation>
					currentRoutes = currentRoutes[camelCaseKey].children!;
				}
			} else {
				throw new Error(`Path ${path} not found in routes`);
			}
		}

		// Iterate through each argument in the provided configuration
		for (const arg in config?.args) {
			// Replace each occurrence of `:arg` in the navigation path with the corresponding argument value
			navigationPath = navigationPath.replace(
				`:${arg}`,
				config.args[arg].toString(),
			);
		}

		// Split the navigation path into segments
		const split = navigationPath.split("/");

		// Iterate through each segment in the split path
		for (const arg in split) {
			// If a segment starts with ":", throw an error because an argument is missing
			if (arg.startsWith(":")) {
				throw new Error(`Argument ${arg} is missing`);
			}
		}

		// If the `withPrefix` configuration option is not set, return the navigation path as is
		if (!config?.withPrefix) return `${navigationPath}`;

		// If the `withPrefix` configuration option is set, return the navigation path prefixed with the protocol and host
		return `${location.protocol}//${location.host}/${navigationPath}`;
	},
	checkBooleanAttribute(path: string, attribute: keyof CbAugmentedRoute) {
		// Function to search for a path in the routes
		const pathSearch = (
			routes: CbAugmentedRoutes,
			path: string,
		): boolean | undefined => {
			// Split the path into segments

			// First transform the path to lower camel case from kebab case
			const formattedPath = path.replace(/-([a-z])/g, (_, letter) =>
				letter.toUpperCase(),
			);

			const split = formattedPath.split("/");
			let route: CbAugmentedRoute | null = null;

			// If the first segment of the path exists in the routes, assign it to the route variable
			if (routes[split[0]]) {
				route = routes[split[0]];
			} else {
				// If the first segment of the path does not exist in the routes, search for it in the "path" attribute of each route
				for (const k in routes) {
					// If a route with the same name is found, assign it to the route variable and break the loop
					if (routes[k].path === split[0]) {
						route = routes[k];
						break;
					}

					// If a route starts with ":", assign it to the route variable and break the loop
					if (routes[k].path?.startsWith(":")) {
						route = routes[k];
						break;
					}
				}
			}

			// If no route is found, return false
			if (!route) return false;

			// If the route has children, recursively search for the path in the children
			if (route.children && split.length > 1) {
				const res = pathSearch(route.children, split.splice(1).join("/"));

				if (res !== undefined) {
					return res;
				}
			}
			// If the route has a "attribute" property and it's true, return true

			// if (route[attribute] !== undefined && route[attribute] === true) {
			// 	return true;
			// }

			// If none of the above conditions are met, return false
			return route[attribute] as boolean | undefined;
		};

		// Remove the leading "/" from the path if it exists
		let pathToCheck = path;
		if (pathToCheck.startsWith("/")) pathToCheck = pathToCheck.substring(1);

		// Strip out query parameters
		pathToCheck = pathToCheck.split("?")[0];

		// Call the pathSearch function with the routes and the path to check
		return pathSearch(routes, pathToCheck);
	},
	showSidebar: (path: string) => {
		return CbRoutesUtils.checkBooleanAttribute(path, "showSidebar");
	},
	hasRealtimeCollaboration: (path: string) => {
		return CbRoutesUtils.checkBooleanAttribute(path, "realtimeCollaboration");
	},
};

function getCalculatedPaths(): CbRoutesPaths {
	const visitRoutes = (
		routes: CbAugmentedRoutes,
		currentPath = "",
	): CbRoutesPaths => {
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		const result: any = {};
		const dynamicRoutes: string[] = [];

		for (const k of Object.keys(routes)) {
			const route = routes[k];
			let path = k === "root" ? "" : k;

			// Convert path from camel case to kebab case
			path = path.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();

			if (path.startsWith(":")) {
				dynamicRoutes.push(path);

				if (dynamicRoutes.length > 1) {
					throw new Error(
						`Only one dynamic route is allowed per segment. Found more than one in ${currentPath}. Found: [${dynamicRoutes}]`,
					);
				}
			}

			const fullPath =
				path && path.length > 0 && path !== "root"
					? `${currentPath}/${path}`
					: currentPath;
			if (route.children) {
				result[k] = {
					...visitRoutes(route.children, fullPath),
					path: fullPath,
				};
			} else {
				result[k] = { path: fullPath };
			}
		}
		return result;
	};

	const result = visitRoutes(routes);
	return result;
}

function convertCustomConfigToRoutes(config: CbAugmentedRoutes): Routes {
	const routes: Routes = [];

	// Iterate over each key in the custom configuration object
	for (const key of Object.keys(config)) {
		const route = config[key];
		let path = route.path || (key === "root" ? "" : key);

		// Convert path from camel case to kebab case
		path = path.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();

		const routeConfig = {
			...(route as Route),
			path: path,
		};

		// If children exist, recursively convert them to routes using the same approach
		if (route.children) {
			routeConfig.children = convertCustomConfigToRoutes(route.children);
		}

		// TODO: Check that the root path doesnt need to be the first
		if (routeConfig.path.startsWith(":")) {
			// Add the configured route to our routes array
			routes.push(routeConfig);
		} else {
			// Add at the start of the array so that the order of the routes is correct
			routes.unshift(routeConfig);
		}
	}

	// Filter out any routes that might have been incorrectly configured (e.g., missing path)
	return routes.filter(
		(r) => r.path !== undefined || r.redirectTo !== undefined,
	);
}
